import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useTrailerTypes, getTrailerTypeLabel } from "@/hooks/useTrailerTypes";

interface InlineTrailerTypeCellProps {
  orderId: string;
  currentValue: string | null;
  fallbackValue?: string | null;
  readOnly?: boolean;
}

export function InlineTrailerTypeCell({
  orderId,
  currentValue,
  fallbackValue,
  readOnly = false,
}: InlineTrailerTypeCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: trailerTypes } = useTrailerTypes();

  const effectiveValue = currentValue ?? fallbackValue ?? null;
  const isEmpty = !effectiveValue;
  const currentLabel = getTrailerTypeLabel(effectiveValue, trailerTypes);

  const handleSave = async (newValue: string) => {
    if (newValue === currentValue) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("orders")
        .update({ trailer_type_id: newValue })
        .eq("id", orderId);

      if (error) throw error;

      const oldLabel = getTrailerTypeLabel(currentValue, trailerTypes);
      const newLabel = getTrailerTypeLabel(newValue, trailerTypes);

      await supabase.from("order_change_log").insert({
        order_id: orderId,
        field_name: "trailer_type",
        old_value: oldLabel || null,
        new_value: newLabel || null,
        changed_by: user?.email || "Unknown",
      });

      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Trailer type updated");
      setIsEditing(false);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  // If has value OR readOnly, show as read-only text
  if (!isEmpty || readOnly) {
    return (
      <span className="text-sm">{currentLabel || (readOnly ? "—" : "Unknown")}</span>
    );
  }

  // If empty and editing
  if (isEditing) {
    return (
      <Select
        onValueChange={(value) => handleSave(value)}
        disabled={isSaving}
      >
        <SelectTrigger className="h-7 text-sm w-28">
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent>
          {trailerTypes?.map((type) => (
            <SelectItem key={type.id} value={type.id}>
              {type.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // If empty and not editing, show clickable placeholder
  return (
    <button
      onClick={() => setIsEditing(true)}
      className="text-sm text-muted-foreground hover:text-foreground hover:underline cursor-pointer"
    >
      + Add
    </button>
  );
}
